// PHASE_0 B0 deliverable 2 — application-composite error-state primitive.
//
// The failed counterpart to Empty/Loading. Rendered when a query/section
// threw — pairs with the customer-web `toUiError` mapping (src/api/errors.ts)
// whose `messageKey` the host resolves to localized copy and passes as
// `title`/`description`. The component owns layout + `role="alert"` + the
// optional retry affordance; it never carries literal copy.

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export interface ErrorStateProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Short, already-localized headline. */
  readonly title: ReactNode;
  /** Optional supporting copy / cause. */
  readonly description?: ReactNode;
  /** Optional retry control (e.g. a <Button onClick={refetch}>). */
  readonly action?: ReactNode;
}

export const ErrorState = forwardRef<HTMLDivElement, ErrorStateProps>(
  ({ title, description, action, className, ...rest }, ref) => (
    <div
      ref={ref}
      role="alert"
      data-component="error-state"
      className={cx("nai-error-state", className)}
      {...rest}
    >
      <p data-slot="error-title" className="nai-error-title">
        {title}
      </p>
      {description !== undefined && (
        <p data-slot="error-description" className="nai-error-description">
          {description}
        </p>
      )}
      {action !== undefined && <div data-slot="error-action">{action}</div>}
    </div>
  ),
);
ErrorState.displayName = "ErrorState";
